/**
 * Staff — Email Delivery Diagnostics
 *
 * One page that answers: "Are our outbound emails actually going out?"
 * Shows the current Resend key health + a rolling log of the last 200
 * send attempts across every flow (welcome, invoice, quote, reset, etc).
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Home, Mail, RefreshCw, CheckCircle2, XCircle, AlertTriangle, Send } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function timeAgo(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const secs = Math.round((now - then) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

export default function EmailDeliveryDiagnostics() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [health, setHealth] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (initial = false) => {
    if (!initial) setRefreshing(true);
    else setLoading(true);
    try {
      const [rHealth, rLog] = await Promise.all([
        fetch(`${API_URL}/api/staff/diagnostics/resend?force=1`, { credentials: 'include' }),
        fetch(`${API_URL}/api/staff/diagnostics/email-log?limit=200`, { credentials: 'include' }),
      ]);
      if (rHealth.ok) setHealth(await rHealth.json());
      if (rLog.ok) setEntries((await rLog.json()).entries || []);
    } catch (e) {
      toast.error('Failed to load diagnostics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load(true);
  }, [user, navigate, load]);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-500">Loading diagnostics…</div>;
  }

  const totalAttempts = entries.length;
  const totalOk = entries.filter((e) => e.ok).length;
  const totalFail = totalAttempts - totalOk;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="border-b sticky top-0 z-20 bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/staff" className="text-gray-500 hover:text-gray-900" data-testid="email-diag-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Link to="/" className="text-gray-500 hover:text-gray-900"><Home className="w-5 h-5" /></Link>
          <Mail className="w-5 h-5 text-gray-500" />
          <h1 className="font-bold text-gray-900">Email Delivery</h1>
          <Link
            to="/staff/email-samples"
            className="ml-auto text-xs text-emerald-700 hover:text-emerald-900 underline inline-flex items-center gap-1"
            data-testid="email-diag-samples-link"
          >
            <Send className="w-3.5 h-3.5" />
            Send sample emails
          </Link>
          <Button variant="outline" size="sm" onClick={() => load(false)} disabled={refreshing} data-testid="email-diag-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {/* Health banner */}
        {health && (
          <Card className={`p-5 border-2 ${health.ok ? 'border-emerald-300 bg-emerald-50' : 'border-red-300 bg-red-50'}`} data-testid="email-diag-health-card">
            <div className="flex items-start gap-3">
              {health.ok
                ? <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                : <XCircle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
              }
              <div className="flex-1">
                <h2 className={`font-bold ${health.ok ? 'text-emerald-900' : 'text-red-900'}`}>
                  {health.ok ? 'Email delivery is HEALTHY' : 'Email delivery is DOWN'}
                </h2>
                <p className={`text-sm mt-1 ${health.ok ? 'text-emerald-800' : 'text-red-800'}`}>
                  {health.ok
                    ? `Resend API responded successfully. Sender: ${health.sender_email}`
                    : (health.error || 'Resend rejected the last send attempt.')}
                </p>
                <div className="text-xs mt-2 grid grid-cols-1 sm:grid-cols-3 gap-1 text-gray-700">
                  <div><b>Key present:</b> {health.key_present ? 'yes' : 'no'}</div>
                  <div><b>Placeholder:</b> {health.key_looks_placeholder ? 'yes ⚠️' : 'no'}</div>
                  <div><b>Checked:</b> {timeAgo(health.checked_at)}</div>
                </div>
                {!health.ok && (
                  <div className="mt-3 p-3 rounded bg-white border border-red-200 text-xs text-red-900">
                    <b>Fix:</b> Set a real key in <code className="bg-red-100 px-1 rounded">/app/backend/.env</code>{' '}
                    (<code className="bg-red-100 px-1 rounded">RESEND_API_KEY=re_...</code>) from{' '}
                    <a className="underline" href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer">resend.com/api-keys</a>,
                    verify <code className="bg-red-100 px-1 rounded">oxresin.co.uk</code> under Domains, then{' '}
                    <code className="bg-red-100 px-1 rounded">sudo supervisorctl restart backend</code>.
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4" data-testid="email-diag-stat-total">
            <div className="text-xs text-gray-500">Attempts (last 200)</div>
            <div className="text-2xl font-bold text-gray-900">{totalAttempts}</div>
          </Card>
          <Card className="p-4" data-testid="email-diag-stat-ok">
            <div className="text-xs text-gray-500">Delivered</div>
            <div className="text-2xl font-bold text-emerald-700">{totalOk}</div>
          </Card>
          <Card className="p-4" data-testid="email-diag-stat-fail">
            <div className="text-xs text-gray-500">Failed</div>
            <div className={`text-2xl font-bold ${totalFail ? 'text-red-700' : 'text-gray-900'}`}>{totalFail}</div>
          </Card>
        </div>

        {/* Log table */}
        <Card className="overflow-hidden">
          <div className="p-4 border-b font-semibold text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-gray-400" />
            Recent attempts
            <span className="text-xs font-normal text-gray-500">(auto-expires after 30 days)</span>
          </div>
          {entries.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500" data-testid="email-diag-empty">
              No email send attempts recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Flow</th>
                    <th className="text-left px-3 py-2">To</th>
                    <th className="text-left px-3 py-2">Subject</th>
                    <th className="text-left px-3 py-2">Result</th>
                  </tr>
                </thead>
                <tbody data-testid="email-diag-log-body">
                  {entries.map((e, idx) => (
                    <tr key={`${e.at || ''}-${e.to || ''}-${e.subject || ''}-${idx}`} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600">{timeAgo(e.at)}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{e.context || 'unknown'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">{e.to || '—'}</td>
                      <td className="px-3 py-2 max-w-md truncate text-gray-700">{e.subject || '—'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {e.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold">
                            <CheckCircle2 className="w-3 h-3" /> Sent
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-red-700 text-xs font-semibold"
                            title={e.error || ''}
                          >
                            <XCircle className="w-3 h-3" /> Failed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
